import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";
import lowerCase from "lodash/lowerCase";
import {Suspense} from "react";
import {Helmet} from "react-helmet-async";
import {useSearchParams} from "react-router";

import {CheckoutContextProvider} from "@/components/context/CheckoutContext.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {AccountSubscriptionType, AccountType} from "@/lib/types/accounts.ts";
import {BillingTabs} from "@/pages/billing/BillingTabs.tsx";

export enum BillingTab {
  SUBSCRIBE = "subscribe",
  PAYMENT_METHOD = "payment-method",
  SUBSCRIPTION_SUMMARY = "subscription-summary",
  MANAGE_SUBSCRIPTION = "manage-subscription",
  MANAGE_ADDONS = "manage-add-ons",
  INVOICES = "invoices",
  ACCOUNT_USAGE = "account-usage",
}

const actionToTab = (action: string | null, hasFreeTrial: boolean): BillingTab => {
  switch (action) {
    case "invoices":
    case "payment-method":
    case "subscription-summary":
      return BillingTab.SUBSCRIPTION_SUMMARY;
    case "manage-subscription":
    case "subscribe":
      return BillingTab.SUBSCRIBE;
    case "manage-add-ons":
      return BillingTab.MANAGE_ADDONS;
    case "account-usage":
      return BillingTab.ACCOUNT_USAGE;
    default:
      if (hasFreeTrial) {
        return BillingTab.SUBSCRIBE;
      }
      return BillingTab.SUBSCRIPTION_SUMMARY;
  }
};

export const BillingPage = () => {
  const {user} = useCurrentUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  let activeTab = actionToTab(searchParams.get("action"), !isEmpty(user?.account.free_trial_expires_at)) as BillingTab;
  // From the point of view of Billing2.0, addons (or pack changes) are managed
  // on the main subscription page. Throughout the codebase we have numerous links
  // to manage add-ons page, and logically for 2.0 they should point to Subscription
  if (
    activeTab === BillingTab.MANAGE_ADDONS &&
    user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0
  ) {
    activeTab = BillingTab.SUBSCRIBE;
  }
  const allTabs = [
    {title: "Payment and Invoices", id: BillingTab.SUBSCRIPTION_SUMMARY},
    {title: "Account Usage", id: BillingTab.ACCOUNT_USAGE},
    {title: "Subscriptions", id: BillingTab.SUBSCRIBE},
  ];
  if (
    user &&
    isEmpty(user?.account.free_trial_expires_at) &&
    [AccountType.REGULAR, AccountType.INTERNAL, AccountType.INVOICED].includes(user?.account.type) &&
    user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0
  ) {
    allTabs.push({title: "Manage Add-Ons", id: BillingTab.MANAGE_ADDONS});
  }
  return (
    <ContentContainer
      title="Manage Subscription"
      actionsBar={
        user?.account &&
        (!isEmpty(user.account.free_trial_expires_at) ||
          user.account.subscription_type === AccountSubscriptionType.BILLING_1_0) && (
          <div className="h-100 d-flex align-items-start align-self-start">
            <div className="fs-7">
              <span className="text-gray-500">Current Plan Type:</span>
              <br />
              <strong>
                {user.account.free_trial_expires_at ? "Free Trial" : capitalize(lowerCase(user.account.plan))}
              </strong>
            </div>
          </div>
        )
      }
      extraHeaderContent={
        <NavTabs
          items={allTabs}
          selectTab={(tab) => setSearchParams({action: tab})}
          selectedId={activeTab}
          variant={"header"}
        />
      }
    >
      <Helmet>
        <title>Billing & Subscriptions</title>
      </Helmet>
      <Suspense>
        <UpErrorBoundary>
          <CheckoutContextProvider>
            <BillingTabs activeTab={activeTab} />
          </CheckoutContextProvider>
        </UpErrorBoundary>
      </Suspense>
    </ContentContainer>
  );
};
